import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable } from '@angular/core';
import { catchError, concatMap } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { LoggerDeploymentsDataApiService } from '@app/api/services/logger-deployments-data-api.service';
import { getLoggerDeployment } from '@app/main/main-screen/store/data/logger-deployments/logger-deployments.actions';
import { IFpLoggerNoiseData } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/interfaces/fp-logger-noise-data';
import {
  FpLoggerNoiseCommentsActionGetData,
  FpLoggerNoiseCommentsActionGetDataSuccesses,
  FpLoggerNoiseCommentsActions,
  FpLoggerNoiseCommentsActionType,
  FpLoggerNoiseCommentsActionUpdateComment,
  FpLoggerNoiseCommentsActionUpdateCommentError,
  FpLoggerNoiseCommentsActionUpdateCommentSuccess,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-comments/services/fp-logger-noise-comments/fp-logger-noise-comments.actions';
import {
  IFpLoggerNoiseCommentsState,
  initialFpLoggerNoiseCommentsState,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-comments/services/fp-logger-noise-comments/fp-logger-noise-comments.state';

@Injectable()
export class FpLoggerNoiseCommentsStore
  extends ComponentStore<IFpLoggerNoiseCommentsState>
  implements IGcComponentStore<FpLoggerNoiseCommentsActions>
{
  public readonly data = inject<IFpLoggerNoiseData>(FLOATING_POPUP_DATA);
  protected readonly store = inject(Store);
  protected readonly loggerDeploymentsApiService = inject(LoggerDeploymentsDataApiService);

  private readonly mainProjectId = this.store.selectSignal(selectMainProjectId);

  constructor() {
    super(initialFpLoggerNoiseCommentsState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly loading$ = this.select(s => s.loading, { equal });
  public readonly saved$ = this.select(s => s.saved, { equal });
  public readonly error$ = this.select(s => s.error, { equal });
  public readonly comment$ = this.select(s => s.data?.loggerDeployment?.comment ?? null, { equal });
  public readonly commentContent$ = this.select(s => s.data?.loggerDeployment?.comment?.content ?? '', { equal });

  public dispatch = this.effect((action$: Observable<FpLoggerNoiseCommentsActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpLoggerNoiseCommentsActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpLoggerNoiseCommentsActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
          case FpLoggerNoiseCommentsActionType.UpdateComment: {
            this.reducerUpdateComment();
            this.effectUpdateComment(action);
            break;
          }
          case FpLoggerNoiseCommentsActionType.UpdateCommentSuccess: {
            this.reducerUpdateCommentSuccess(action);
            break;
          }
          case FpLoggerNoiseCommentsActionType.UpdateCommentError: {
            this.reducerUpdateCommentError(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({
      loading: true,
    });
  }

  private reducerGetDataSuccesses(action: FpLoggerNoiseCommentsActionGetDataSuccesses) {
    this.patchState({
      date: action.date,
      data: action.data,
      loading: false,
    });
  }

  private reducerUpdateComment() {
    this.patchState({
      loading: true,
      saved: false,
      error: null,
    });
  }

  private reducerUpdateCommentSuccess(action: FpLoggerNoiseCommentsActionUpdateCommentSuccess) {
    const state = this.get();

    if (!state.data?.loggerDeployment) {
      this.patchState({ loading: false });
      return;
    }

    this.patchState({
      loading: false,
      saved: true,
      error: null,
      data: {
        ...state.data,
        loggerDeployment: {
          ...state.data.loggerDeployment,
          comment: action.comment,
        },
      },
    });

    this.store.dispatch(getLoggerDeployment({ loggerDeploymentId: state.data.loggerDeployment.id }));
  }

  private reducerUpdateCommentError(action: FpLoggerNoiseCommentsActionUpdateCommentError) {
    this.patchState({
      loading: false,
      saved: false,
      error: action.error,
    });
  }

  private effectGetData(action: FpLoggerNoiseCommentsActionGetData) {
    this.dispatch({
      type: FpLoggerNoiseCommentsActionType.GetDataSuccesses,
      date: action.date,
      data: action.popupMainData,
    });
  }

  private effectUpdateComment(action: FpLoggerNoiseCommentsActionUpdateComment) {
    const state = this.get();
    const projectId = this.mainProjectId();
    const deployment = state.data?.loggerDeployment;

    if (!projectId || !deployment) {
      this.patchState({ loading: false });

      return;
    }

    this.loggerDeploymentsApiService
      .updateComment(projectId, deployment.id, { content: action.content })
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpLoggerNoiseCommentsActionType.UpdateCommentError,
            error,
          });

          return EMPTY;
        })
      )
      .subscribe(response => {
        this.dispatch({
          type: FpLoggerNoiseCommentsActionType.UpdateCommentSuccess,
          comment: response.data,
        });
      });
  }
}
